import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Save, Edit, X, MapPin, Phone, Globe, AlertTriangle } from "lucide-react";
import { usePermissions } from "../lib/permissions";
import { resolveImage } from "../lib/images";
import PhotoUpload from "../components/PhotoUpload";
import MaintenanceContacts from "../components/MaintenanceContacts";
import VaccineLibrary from "../components/VaccineLibrary";

const TEXT_FIELDS = [
  { key: "name", label: "Stable name *", required: true },
  { key: "country", label: "Country *", required: true },
  { key: "area_region", label: "Area / Region", placeholder: "Markopoulo, Attica" },
  { key: "address_street", label: "Street" },
  { key: "address_number", label: "Number" },
  { key: "address_city", label: "City" },
  { key: "address_postal_code", label: "Postal code" },
  { key: "address_country", label: "Address country" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Contact email" },
  { key: "website_url", label: "Website / social URL" },
  { key: "google_maps_url", label: "Google Maps URL", placeholder: "https://maps.app.goo.gl/…" },
  { key: "emergency_contact_name", label: "Emergency contact name" },
  { key: "emergency_contact_phone", label: "Emergency contact phone" },
];

export default function Settings() {
  const { canEdit, role } = usePermissions();
  // BUG-005: operational stable settings are editable by admin AND manager.
  // (Billing / plan / delete-stable / API keys remain admin-only — none of
  // those controls live on this page yet.)
  const canEditStable = role === "admin" || role === "super_admin" || role === "manager";
  const [stable, setStable] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toast, setToast] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/stables/me");
      setStable(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setForm({
      name: stable.name || "", country: stable.country || "",
      area_region: stable.area_region || "",
      address_street: stable.address_street || "",
      address_number: stable.address_number || "",
      address_city: stable.address_city || "",
      address_postal_code: stable.address_postal_code || "",
      address_country: stable.address_country || "",
      phone: stable.phone || "",
      email: stable.email || "",
      website_url: stable.website_url || "",
      google_maps_url: stable.google_maps_url || "",
      emergency_contact_name: stable.emergency_contact_name || "",
      emergency_contact_phone: stable.emergency_contact_phone || "",
      notes: stable.notes || "",
      logo_url: stable.logo_url || null,
      banner_url: stable.banner_url || null,
    });
    setSaveError("");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === undefined) return;
        payload[k] = v;
      });
      await api.patch("/stables/me", payload);
      setEditing(false);
      setToast("Stable details updated");
      setTimeout(() => setToast(""), 2200);
      await load();
    } catch (err) {
      setSaveError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const banner = resolveImage(stable?.banner_url);
  const logo = resolveImage(stable?.logo_url);
  const composedAddress = stable
    ? [
        [stable.address_street, stable.address_number].filter(Boolean).join(" "),
        [stable.address_postal_code, stable.address_city].filter(Boolean).join(" "),
        stable.address_country,
      ].filter(Boolean).join(", ")
    : "";

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <div className="se-label">Settings</div>
        <h1 className="font-heading mt-1" style={{ fontSize: 32 }}>Stable details</h1>
        <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
          Edit your stable profile, branding, address, contacts and operational notes.
        </p>
      </div>

      {error && <div className="se-card mb-4" style={{ color: "#922B21" }} data-testid="settings-error">{error}</div>}
      {toast && (
        <div className="mb-4 se-card flex items-center gap-2" style={{ background: "rgba(74,124,89,0.12)", color: "#2D5E3A" }} data-testid="settings-toast">
          <Save size={14} /> {toast}
        </div>
      )}

      {stable && !editing && (
        <>
          <div className="se-card mb-6 p-0 overflow-hidden" data-testid="stable-banner-card">
            <div
              style={{
                aspectRatio: "4 / 1",
                background: banner
                  ? `center/cover no-repeat url(${banner})`
                  : "linear-gradient(135deg, #7C5C3A 0%, #B8895A 100%)",
                position: "relative",
              }}
            >
              {logo && (
                <div
                  style={{
                    position: "absolute", bottom: -36, left: 24,
                    width: 72, height: 72, borderRadius: 16,
                    background: "#FDFAF6", padding: 6,
                    border: "0.5px solid rgba(212,169,106,0.4)",
                  }}
                  data-testid="stable-logo-thumb"
                >
                  <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} />
                </div>
              )}
            </div>
            <div className="px-6 pt-12 pb-6">
              <h2 className="font-heading" style={{ fontSize: 22 }}>{stable.name}</h2>
              <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
                {stable.area_region ? `${stable.area_region} · ` : ""}{stable.country}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="se-card">
              <div className="se-label mb-3">Address</div>
              <div className="flex items-start gap-2">
                <MapPin size={16} style={{ color: "#7C5C3A", marginTop: 2 }} />
                <div>
                  <div>{composedAddress || "—"}</div>
                  {stable.google_maps_url && (
                    <a href={stable.google_maps_url} target="_blank" rel="noreferrer" className="se-link text-sm" data-testid="stable-maps-link">
                      Open in Google Maps ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="se-card">
              <div className="se-label mb-3">Contact</div>
              <div className="space-y-2 text-sm">
                {stable.phone && <div className="flex items-center gap-2"><Phone size={14} style={{ color: "#7C5C3A" }} /> {stable.phone}</div>}
                {stable.email && <div className="flex items-center gap-2"><span>✉</span> {stable.email}</div>}
                {stable.website_url && (
                  <div className="flex items-center gap-2">
                    <Globe size={14} style={{ color: "#7C5C3A" }} />
                    <a href={stable.website_url} target="_blank" rel="noreferrer" className="se-link">{stable.website_url}</a>
                  </div>
                )}
              </div>
            </div>
            <div className="se-card md:col-span-2">
              <div className="se-label mb-3 flex items-center gap-2"><AlertTriangle size={12} /> Emergency contact</div>
              <div className="text-sm">
                {stable.emergency_contact_name || "—"}
                {stable.emergency_contact_phone && <span style={{ color: "#8C9E8A" }}> · {stable.emergency_contact_phone}</span>}
              </div>
            </div>
            {stable.notes && (
              <div className="se-card md:col-span-2">
                <div className="se-label mb-2">Notes</div>
                <p className="text-sm whitespace-pre-wrap">{stable.notes}</p>
              </div>
            )}
          </div>

          {canEdit && canEditStable && (
            <div className="flex justify-end mt-5">
              <button className="se-btn" onClick={startEdit} data-testid="settings-edit-button">
                <Edit size={14} /> Edit stable
              </button>
            </div>
          )}

          <div className="mt-6">
            <MaintenanceContacts testid="maintenance-contacts" />
          </div>

          <VaccineLibrary testid="vaccine-library" />
        </>
      )}

      {stable && editing && (
        <form className="se-card" onSubmit={save} data-testid="settings-edit-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading" style={{ fontSize: 22 }}>Edit stable</h3>
            <button type="button" className="se-btn se-btn-ghost" onClick={() => setEditing(false)}>
              <X size={14} /> Cancel
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <div className="se-label mb-2">Logo</div>
              <PhotoUpload
                value={form.logo_url}
                onChange={(url) => setForm({ ...form, logo_url: url })}
                purpose="logo"
                aspect="square"
                hint="Square crop · 400×400 recommended · max 2MB"
                testid="settings-logo"
              />
            </div>
            <div>
              <div className="se-label mb-2">Banner photo</div>
              <PhotoUpload
                value={form.banner_url}
                onChange={(url) => setForm({ ...form, banner_url: url })}
                purpose="stable_banner"
                aspect="banner"
                hint="Wide crop · 1200×400 recommended · max 3MB"
                testid="settings-banner"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TEXT_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="se-label block mb-1">{f.label}</label>
                <input
                  className="se-input"
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  required={!!f.required}
                  placeholder={f.placeholder || ""}
                  data-testid={`settings-edit-${f.key}`}
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="se-label block mb-1">Stable notes</label>
              <textarea
                className="se-input"
                style={{ height: 90, padding: "10px 14px" }}
                placeholder="Access instructions, seasonal info, special rules…"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="settings-edit-notes"
              />
            </div>
          </div>

          {saveError && (
            <div className="mt-4 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="settings-edit-error">
              {saveError}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-5">
            <button type="button" className="se-btn se-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" className="se-btn" disabled={saving} data-testid="settings-edit-submit">
              {saving ? "Saving…" : (<><Save size={14} /> Save changes</>)}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
